import { z } from "zod";
import { sanitizeInput } from "../utils/sanitization.js";
const s = (schema) => schema.transform(sanitizeInput);
const sOpt = z.string().nullable().optional().transform((v) => (v ? sanitizeInput(v) : v));
export const spaceTypes = ["home", "floor", "room", "container"];
export const invitationStatuses = ["pending", "accepted", "rejected"];
export const flowFrequencies = ["daily", "weekly", "monthly", "quarterly", "yearly"];
// Space Intent: current/desired state options
export const spaceStates = ["organized", "somewhat_cluttered", "cluttered", "overwhelmed", "empty", "in_progress"];
// Space Intent: activity options by room type — helps users define how they use a space
export const SPACE_ACTIVITIES = {
    kitchen: [
        { label: "Daily cooking", value: "daily_cooking" },
        { label: "Meal prep", value: "meal_prep" },
        { label: "Entertaining", value: "entertaining" },
        { label: "Food storage", value: "food_storage" },
        { label: "Coffee/tea station", value: "coffee_tea" },
        { label: "Kids' snacks & lunches", value: "kids_food" },
    ],
    "living room": [
        { label: "Relaxing", value: "relaxing" },
        { label: "Entertaining guests", value: "entertaining" },
        { label: "TV & movies", value: "media" },
        { label: "Reading", value: "reading" },
        { label: "Kids' play area", value: "kids_play" },
        { label: "Working from home", value: "wfh" },
    ],
    bedroom: [
        { label: "Sleep & rest", value: "sleep" },
        { label: "Getting ready", value: "getting_ready" },
        { label: "Reading & winding down", value: "reading" },
        { label: "Working from home", value: "wfh" },
        { label: "Kids' room", value: "kids_room" },
        { label: "Storage", value: "storage" },
    ],
    bathroom: [
        { label: "Personal care", value: "personal_care" },
        { label: "Morning routine", value: "morning_routine" },
        { label: "Grooming & beauty", value: "grooming" },
        { label: "Kids' bath time", value: "kids_bath" },
    ],
    office: [
        { label: "Focused work", value: "focused_work" },
        { label: "Video calls", value: "video_calls" },
        { label: "Paperwork & filing", value: "paperwork" },
        { label: "Crafts & hobbies", value: "crafts" },
        { label: "Study", value: "study" },
    ],
    garage: [
        { label: "Car parking", value: "parking" },
        { label: "Tool workshop", value: "workshop" },
        { label: "Sports equipment", value: "sports" },
        { label: "Seasonal storage", value: "seasonal_storage" },
        { label: "Hobby space", value: "hobby" },
    ],
    closet: [
        { label: "Daily clothing", value: "daily_clothing" },
        { label: "Seasonal rotation", value: "seasonal_rotation" },
        { label: "Accessories & shoes", value: "accessories" },
        { label: "Linens & towels", value: "linens" },
        { label: "General storage", value: "storage" },
    ],
    pantry: [
        { label: "Daily staples", value: "daily_staples" },
        { label: "Bulk buying", value: "bulk_buying" },
        { label: "Snacks & quick meals", value: "snacks" },
        { label: "Baking supplies", value: "baking" },
        { label: "Pet food", value: "pet_food" },
    ],
    "laundry room": [
        { label: "Laundry", value: "laundry" },
        { label: "Ironing & folding", value: "ironing" },
        { label: "Cleaning supplies", value: "cleaning_supplies" },
        { label: "Stain treatment", value: "stain_treatment" },
    ],
    "dining room": [
        { label: "Dining", value: "dining" },
        { label: "Entertaining", value: "entertaining" },
        { label: "Homework & projects", value: "homework" },
        { label: "Tableware storage", value: "tableware" },
    ],
    basement: [
        { label: "Storage", value: "storage" },
        { label: "Recreation", value: "recreation" },
        { label: "Workshop", value: "workshop" },
        { label: "Gym", value: "gym" },
        { label: "Laundry", value: "laundry" },
    ],
    attic: [
        { label: "Seasonal storage", value: "seasonal_storage" },
        { label: "Keepsakes & memories", value: "keepsakes" },
        { label: "Rarely used items", value: "rarely_used" },
    ],
    // Fallback for generic/unknown room types
    _default: [
        { label: "Daily use", value: "daily_use" },
        { label: "Storage", value: "storage" },
        { label: "Work & productivity", value: "work" },
        { label: "Relaxation", value: "relaxation" },
        { label: "Kids' space", value: "kids" },
        { label: "Entertaining", value: "entertaining" },
    ],
};
/**
 * Space Intent: default activities suggested per room type.
 *
 * These are treated as *weak* defaults — perceived more softly than a user's
 * own choice. They are NEVER persisted as if the user picked them; instead the
 * default is resolved at read time (see `resolveSpaceActivities`) and tagged
 * `source: "default"`. The moment a user makes their own selection, their value
 * takes over and is tagged `source: "user"`.
 *
 * Every value here MUST exist in the corresponding SPACE_ACTIVITIES[roomType]
 * list so the default lines up with the options the user sees. Room types not
 * listed here simply have no default.
 */
export const DEFAULT_ACTIVITIES_BY_ROOM_TYPE = {
    kitchen: ["meal_prep"],
    "dining room": ["dining"],
    bedroom: ["sleep"],
    bathroom: ["personal_care"],
    "laundry room": ["laundry"],
};
/**
 * Normalize a space's room key the same way the UI does (lowercase, trailing
 * number stripped, e.g. "Bedroom 2" -> "bedroom").
 */
export function normalizeRoomKey(roomType, name) {
    return (roomType ?? name ?? "").toLowerCase().replace(/\s*\d+$/, "");
}
/**
 * Resolve the activities for a space, applying weak room-type defaults only when
 * the user has NOT set their own. Returns each value tagged with its provenance.
 *
 * - If the user has any activities set -> return those, all tagged `user`.
 * - Otherwise -> return the room-type default (if any), tagged `default`.
 * - Otherwise -> empty array.
 *
 * The default is never written to storage by this function; persistence of a
 * default only happens if/when the user explicitly saves their intent.
 */
export function resolveSpaceActivities(space) {
    const userActivities = space.activities ?? [];
    if (userActivities.length > 0) {
        return userActivities.map((value) => ({ value, source: "user" }));
    }
    const key = normalizeRoomKey(space.roomType, space.name);
    const defaults = DEFAULT_ACTIVITIES_BY_ROOM_TYPE[key] ?? [];
    return defaults.map((value) => ({ value, source: "default" }));
}
/** True when a space has no user-defined intent and is relying on defaults (or has none). */
export function hasUserDefinedActivities(space) {
    return (space.activities?.length ?? 0) > 0;
}
/**
 * True when the *user* has explicitly defined any intent for a space — a
 * free-text purpose, selected activities, or a current/desired state. A weak
 * room-type default does NOT count here (see `spaceHasIntent` for that).
 *
 * Use this when you specifically need to know whether the user has taken
 * action (e.g. onboarding progress, "you've set this up" states).
 */
export function spaceHasUserIntent(space) {
    return !!(space.purpose ||
        (space.activities?.length ?? 0) > 0 ||
        space.currentState ||
        space.desiredState);
}
/**
 * True when a space has *effective* intent for the purpose of deciding whether
 * to show suggestions and whether to suppress "set up your space" nudges.
 *
 * A space has effective intent when EITHER:
 *  - the user has explicitly defined intent (`spaceHasUserIntent`), OR
 *  - the space carries a sensible weak room-type default (e.g. a bedroom
 *    defaults to "sleep") via `resolveSpaceActivities`.
 *
 * This is the definition every nudge/suggestion surface should share: there's
 * no value in nagging a user to "define intent" for a space that already has an
 * obvious purpose from its room type. Only spaces with neither user intent nor
 * a default are considered to genuinely lack intent.
 */
export function spaceHasIntent(space) {
    if (spaceHasUserIntent(space))
        return true;
    return (resolveSpaceActivities({
        roomType: space.roomType ?? null,
        name: space.name ?? null,
        activities: space.activities ?? null,
    }).length > 0);
}
export const CreateSpaceSchema = z.object({
    name: s(z.string().min(1).max(100)),
    type: z.enum(spaceTypes),
    parentId: z.string().uuid().nullable().optional(),
    icon: z.string().max(50).nullable().optional(),
    roomType: z.string().max(50).nullable().optional(),
    description: sOpt,
    // Space Intent fields
    purpose: sOpt,
    activities: z.array(z.string().max(50)).max(10).nullable().optional(),
    currentState: z.enum(spaceStates).nullable().optional(),
    desiredState: z.enum(spaceStates).nullable().optional(),
});
export const UpdateSpaceSchema = CreateSpaceSchema.partial();
export const BulkCreateSpacesSchema = z.object({
    parentId: z.string().uuid().nullable().optional(),
    spaces: z.array(z.object({
        name: s(z.string().min(1).max(100)),
        type: z.enum(spaceTypes).default("room"),
        icon: z.string().max(50).nullable().optional(),
        roomType: z.string().max(50).nullable().optional(),
        quantity: z.number().int().min(1).max(20).default(1),
    })).min(1).max(50),
});
export const AVAILABLE_FIELDS = [
    { key: "status", label: "Status" },
    { key: "notes", label: "Notes" },
    { key: "quantity", label: "Quantity" },
    { key: "manufacturer", label: "Manufacturer" },
    { key: "purchaseDate", label: "Purchase Date" },
    { key: "careInstructions", label: "Care Instructions" },
    { key: "lastUsedDate", label: "Last Used Date" },
    { key: "estimatedValue", label: "Estimated Value" },
];
export const CreateItemSchema = z.object({
    name: s(z.string().min(1).max(200)),
    description: sOpt,
    barcode: z.string().nullable().optional(),
    photoUrl: z.string().nullable().optional(),
    quantity: z.number().int().min(0).default(1),
    status: z.string().nullable().optional(),
    manufacturer: z.string().nullable().optional(),
    purchaseDate: z.string().nullable().optional(),
    careInstructions: sOpt,
    lastUsedDate: z.string().nullable().optional(),
    spaceId: z.string().uuid().nullable().optional(),
    tags: z.array(z.string().max(50).transform((t) => t.trim().toLowerCase())).max(20).default([]),
    notes: sOpt,
    lowStockThreshold: z.number().int().min(0).nullable().optional(),
    estimatedValue: z.number().int().min(0).nullable().optional(),
    receiptPhotoUrl: z.string().nullable().optional(),
    warrantyPhotoUrl: z.string().nullable().optional(),
});
export const UpdateItemSchema = CreateItemSchema.partial();
/**
 * Resolve a batch of scan-detected items into real inventory (F-f / F1
 * "create-or-match"). Dedupes each detected name against existing items in the
 * target space (case-insensitive); matched names resolve to the existing item,
 * the rest are created. `create: false` previews the match/create split without
 * writing; `create: true` performs the creation and returns all resolved ids.
 */
export const ResolveScanItemsSchema = z.object({
    detected: z.array(z.object({
        name: s(z.string().min(1).max(200)),
        description: sOpt,
        quantity: z.number().int().min(0).default(1),
        tags: z.array(z.string().max(50).transform((t) => t.trim().toLowerCase())).max(20).default([]),
    })).min(1).max(50),
    /** Space to dedupe within and place newly-created items into. Null = match
     *  across the whole household and create the new items unplaced. */
    targetSpaceId: z.string().uuid().nullable().optional(),
    /** false = preview only (default); true = create the new items now. */
    create: z.boolean().default(false),
});
/**
 * Apply a confirmed Quick Reset checklist to real data (QR-4 / decisions Q3+Q4).
 * Each entry pairs a detected item name with the AI-suggested "home" (a free-text
 * location string). The server create-or-matches the location to a real space
 * (by name, case-insensitive; created as a room when no match), create-or-matches
 * the item within that space, records a "moved" history row when an existing item
 * changes space, and logs a completion event for streaks/analytics.
 * This is opt-in: the checklist-only path never calls it.
 */
export const ApplyQuickResetSchema = z.object({
    entries: z.array(z.object({
        /** Detected out-of-place item name. */
        name: s(z.string().min(1).max(200)),
        /** AI-suggested home / location string (matched or created as a space). */
        location: s(z.string().min(1).max(200)),
    })).min(1).max(50),
});
export const BulkUpdateItemsSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(100),
    spaceId: z.string().uuid().nullable().optional(),
    tags: z.array(z.string().max(50).transform((t) => t.trim().toLowerCase())).optional(),
    tagUpdateMode: z.enum(["add", "replace"]).default("replace"),
    // Bulk-set the last-used date (ISO "YYYY-MM-DD"), or null to clear it. This is
    // the key input for the declutter flow: it lets users correct the last-used
    // signal for many items at once instead of one edit form at a time.
    lastUsedDate: z.string().nullable().optional(),
    // Bulk Re-locate designation (P0-B, R-B1 "incl. bulk/multi-select"). true =
    // flag "I want to move this", false = clear the flag. Independent of location.
    relocate: z.boolean().optional(),
});
/** Toggle a single item's Re-locate designation (P0-B). */
export const SetRelocateSchema = z.object({
    relocate: z.boolean(),
});
/**
 * Planned moves (docs/organize-declutter-onboarding-design.md §6b). Record that
 * each item's destination is decided but the item hasn't been moved yet. The
 * item's location does not change until the move is confirmed.
 */
export const PlanMovesSchema = z.object({
    moves: z.array(z.object({
        itemId: z.string().uuid(),
        spaceId: z.string().uuid(),
    })).min(1).max(200),
});
/** Confirm / decide again / drop a set of planned moves. */
export const PlannedMoveItemsSchema = z.object({
    itemIds: z.array(z.string().uuid()).min(1).max(200),
});
/**
 * Record a completed triage pass (P0-C, R-C6). The label is OPTIONAL free text
 * with no identity (never matched/deduped/aggregated — guardrail). `items`
 * carries the decision (bucket) for each triaged item.
 */
export const CreateTriagePassSchema = z.object({
    spaceId: z.string().uuid(),
    label: z.string().max(120).nullable().optional(),
    context: z.string().max(500).nullable().optional(),
    photoKeys: z.array(z.string().max(500)).max(10).nullable().optional(),
    items: z.array(z.object({
        itemId: z.string().uuid(),
        bucket: z.enum(["belongs", "wrong_container", "doesnt_belong"]),
    })).max(200).default([]),
});
/**
 * One item within an in-progress triage session (P0-C interrupted-triage
 * persistence). Mirrors the client's working item: the tentative bucket plus the
 * ❌-resolution choice, if any. Names are sanitized like other free text.
 */
export const TriageSessionItemSchema = z.object({
    itemId: z.string().uuid(),
    name: s(z.string().max(300)),
    bucket: z.enum(["belongs", "wrong_container", "doesnt_belong"]),
    suggestedContainer: z.string().max(300).optional(),
    suggestedContainerSpaceId: z.string().uuid().nullable().optional(),
    resolveSpaceId: z.string().uuid().nullable().optional(),
    resolveUnsure: z.boolean().optional(),
});
/**
 * Upsert the server-side DRAFT of an in-progress triage sort so it survives the
 * user leaving mid-sort (interrupted triage). One open draft per (household,
 * space): saving again overwrites it. This is NOT a completed pass — the draft
 * is deleted once the sort finishes into a pass (or is abandoned).
 */
export const SaveTriageSessionSchema = z.object({
    spaceId: z.string().uuid(),
    phase: z.enum(["sort", "resolve", "done"]),
    cursor: z.number().int().min(0).max(500).default(0),
    context: z.string().max(500).nullable().optional(),
    photoKeys: z.array(z.string().max(500)).max(10).nullable().optional(),
    label: z.string().max(120).nullable().optional(),
    items: z.array(TriageSessionItemSchema).min(1).max(200),
});
export const BulkDeleteItemsSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(100),
});
export const CreateInvitationSchema = z.object({
    email: z.string().email(),
});
export const homeTypes = ["apartment", "house", "condo", "other"];
export const tidinesLevels = ["organized", "some_clutter", "overwhelmed"];
export const HomeProfileSchema = z.object({
    homeType: z.enum(homeTypes),
    tidiness: z.enum(tidinesLevels),
});
export const flowTrialStatuses = ["trial", "kept", "discarded"];
export const CreateGuidedFlowSchema = z.object({
    title: s(z.string().min(1).max(200)),
    description: sOpt,
    itemId: z.string().uuid().nullable().optional(),
    spaceId: z.string().uuid().nullable().optional(),
    frequency: z.enum(flowFrequencies),
    nextDueDate: z.coerce.date(),
    estimatedMinutes: z.number().int().min(1).max(480).nullable().optional(),
    tasks: z.array(z.object({
        id: z.string(),
        label: s(z.string().min(1)),
        isCompleted: z.boolean().default(false),
    })).default([]),
    // Trial fields
    trialStatus: z.enum(flowTrialStatuses).default("trial"),
    trialReviewAfter: z.number().int().min(2).max(20).default(3),
});
export const UpdateGuidedFlowSchema = CreateGuidedFlowSchema.partial().extend({
    // Allow setting trial decision
    trialStatus: z.enum(flowTrialStatuses).optional(),
    trialDecidedAt: z.coerce.date().nullable().optional(),
    missedCount: z.number().int().min(0).optional(),
});
export const UpdateSettingsSchema = z.object({
    quickAddFields: z.array(z.string()).optional(),
    aiEnabled: z.boolean().optional(),
    promotedTags: z.array(z.string().max(50)).optional(),
});
// Declutter "not used in" windows, in months (1–120). Separate from
// UpdateSettingsSchema because this is a per-member viewing preference that any
// household member can change, not an owner-only household setting.
export const UpdateDeclutterTimelineSchema = z.object({
    declutterMonths: z.number().int().min(1).max(120).optional(),
    declutterSeasonalMonths: z.number().int().min(1).max(120).optional(),
});
export const onboardingGoalOptions = ["organize_home", "track_stuff", "prepare_move", "insurance_docs", "declutter", "find_things"];
export const OnboardingGoalsSchema = z.object({
    goals: z.array(z.enum(onboardingGoalOptions)).min(1).max(6),
});
// Stress Spaces — captured during onboarding
export const STRESS_SPACE_OPTIONS = [
    { value: "kitchen", label: "Kitchen", emoji: "🍳" },
    { value: "living_room", label: "Living Room", emoji: "🛋️" },
    { value: "bedroom", label: "Bedroom", emoji: "🛏️" },
    { value: "closet", label: "Closet / Wardrobe", emoji: "👔" },
    { value: "bathroom", label: "Bathroom", emoji: "🚿" },
    { value: "garage", label: "Garage", emoji: "🚗" },
    { value: "office", label: "Home Office", emoji: "💻" },
    { value: "basement", label: "Basement", emoji: "🏚️" },
    { value: "attic", label: "Attic / Storage", emoji: "📦" },
    { value: "pantry", label: "Pantry", emoji: "🥫" },
    { value: "laundry_room", label: "Laundry Room", emoji: "🧺" },
    { value: "entryway", label: "Entryway / Mudroom", emoji: "🚪" },
    { value: "dining_room", label: "Dining Room", emoji: "🍽️" },
    { value: "kids_room", label: "Kids' Room", emoji: "🧸" },
];
export const StressSpaceEntry = z.object({
    room: z.string().min(1).max(50),
    reason: z.string().max(200).default(""),
});
export const StressSpacesSchema = z.object({
    spaces: z.array(StressSpaceEntry).max(10),
});
// Anonymous install id used to stitch the pre-signup funnel (anonymous_events)
// to the post-signup funnel. Optional, random, non-identifying; length-capped
// to match the anon-events endpoint's validation.
const AnonIdField = z.string().min(1).max(128).optional();
export const RegisterSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8)
        .regex(/[A-Z]/, "Must contain an uppercase letter")
        .regex(/[0-9]/, "Must contain a number"),
    goals: z.array(z.enum(onboardingGoalOptions)).optional(),
    stressSpaces: z.array(StressSpaceEntry).optional(),
    anonId: AnonIdField,
});
export const LoginSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
export const GoogleAuthSchema = z.object({
    idToken: z.string().min(1),
    goals: z.array(z.enum(onboardingGoalOptions)).optional(),
    stressSpaces: z.array(StressSpaceEntry).optional(),
    anonId: AnonIdField,
});
export const AppleAuthSchema = z.object({
    identityToken: z.string().min(1),
    fullName: z.object({
        givenName: z.string().nullable().optional(),
        familyName: z.string().nullable().optional(),
    }).nullable().optional(),
    goals: z.array(z.enum(onboardingGoalOptions)).optional(),
    stressSpaces: z.array(StressSpaceEntry).optional(),
    anonId: AnonIdField,
});
export const VerifyEmailSchema = z.object({
    token: z.string().min(1),
});
export const ForgotPasswordSchema = z.object({
    email: z.string().email(),
});
export const ResetPasswordSchema = z.object({
    token: z.string().min(1),
    password: z.string().min(8)
        .regex(/[A-Z]/, "Must contain an uppercase letter")
        .regex(/[0-9]/, "Must contain a number"),
});
// --- Smart Search ---
export const SearchQuerySchema = z.object({
    q: z.string().min(1).max(500),
});
// --- Notifications ---
export const notificationTypes = ["low_stock", "unused_item", "packing_reminder", "detection_nudge", "goal_suggestion", "trial_check_in"];
// --- Packing Lists ---
export const packingListStatuses = ["active", "completed", "archived"];
export const CreatePackingListSchema = z.object({
    name: s(z.string().min(1).max(200)),
    description: sOpt,
});
export const UpdatePackingListSchema = z.object({
    name: s(z.string().min(1).max(200)).optional(),
    description: sOpt,
    status: z.enum(packingListStatuses).optional(),
});
export const CreatePackingBagSchema = z.object({
    name: s(z.string().min(1).max(100)),
});
export const AddPackingListItemSchema = z.object({
    itemId: z.string().uuid().nullable().optional(),
    customName: s(z.string().min(1).max(200)).optional(),
    bagId: z.string().uuid().nullable().optional(),
});
export const UpdatePackingListItemSchema = z.object({
    bagId: z.string().uuid().nullable().optional(),
    packed: z.boolean().optional(),
});
// --- Low Stock ---
export const UpdateLowStockThresholdSchema = z.object({
    lowStockThreshold: z.number().int().min(0).nullable(),
});
// --- Seasons / Boxes ---
export const seasons = ["spring", "summer", "fall", "winter", "holiday", "moving", "guest"];
export const CreateBoxSchema = z.object({
    label: s(z.string().min(1).max(200)),
    number: z.number().int().min(1),
    season: z.enum(seasons).nullable().optional(),
    spaceId: z.string().uuid().nullable().optional(),
});
export const UpdateBoxSchema = CreateBoxSchema.partial();
export const AddBoxItemSchema = z.object({
    itemId: z.string().uuid(),
});
// --- Space Photo ---
export const UpdateSpacePhotoSchema = z.object({
    photoUrl: z.string().nullable().optional(),
});
// --- Organizing Goals & Placement Trials ---
export const organizingGoalStatuses = ["active", "achieved", "paused", "abandoned"];
export const trialStatuses = ["created", "confirmed_moved", "in_progress", "adopted", "reverted", "expired_not_started"];
// Focused goal kinds. Each keeps the goal detail page to a single intent
// instead of showing every panel (placement + routine + declutter) at once.
//   find_home  — decide where item(s) should live (placement; default)
//   reorganize — rework how a space is organized (placement machinery)
//   routine    — build a repeatable routine/habit (process trials)
//   declutter  — reduce stuff / let things go (declutter suggestions)
// NOTE: "routine" is retained here for reads/updates so existing/legacy
// routine-type goals still validate, but it is NO LONGER a creatable goal type
// (a routine is repeat maintenance, not one discrete goal — see the flow
// restructure spec §7.2). New goals must be one of `createGoalTypes`.
export const organizingGoalTypes = ["find_home", "reorganize", "routine", "declutter"];
/** Goal types a user can create. Excludes "routine" — routines are a separate flow. */
export const createGoalTypes = ["find_home", "reorganize", "declutter"];
export const CreateOrganizingGoalSchema = z.object({
    title: s(z.string().min(1).max(200)),
    description: sOpt,
    activityLocationId: z.string().uuid().nullable().optional(),
    type: z.enum(createGoalTypes).optional(),
});
export const UpdateOrganizingGoalSchema = z.object({
    title: s(z.string().min(1).max(200)).optional(),
    description: sOpt,
    activityLocationId: z.string().uuid().nullable().optional(),
    status: z.enum(organizingGoalStatuses).optional(),
    type: z.enum(organizingGoalTypes).optional(),
});
export const LinkGoalItemsSchema = z.object({
    itemIds: z.array(z.string().uuid()).min(1).max(50),
});
export const CreatePlacementTrialSchema = z.object({
    targetSpaceId: z.string().uuid(),
    itemIds: z.array(z.string().uuid()).min(1).max(50),
    // Optional per-item destinations (F3/F-d). Maps an itemId (which must be in
    // itemIds) to its own target space; any item not listed here falls back to the
    // trial-level targetSpaceId. Omitting this entirely = classic single-target
    // trial, so existing callers are unaffected.
    itemTargets: z.record(z.string().uuid(), z.string().uuid()).optional(),
    // "Make this the home" (F4/F-b). When true, the placement is adopted
    // immediately instead of entering the 2-week trial loop: items move now and
    // the trial is written as `adopted` in one step (immediate-adopt), keeping a
    // single history model and the goal→routine handoff. Omitting/false = the
    // classic trial loop, so existing callers are unaffected.
    permanent: z.boolean().optional().default(false),
});
export const ExtendTrialSchema = z.object({
    days: z.number().int().min(1).max(30).default(14),
});
export const TrialDecisionSchema = z.object({
    outcomeNote: sOpt,
});
// --- Phase 2: Intelligence ---
export const CreateFrictionFlagSchema = z.object({
    note: sOpt,
});
export const AISuggestPlacementSchema = z.object({
    goalId: z.string().uuid(),
});
/**
 * Flow #3 — "Place a new item".
 * Goal-free, single-item placement suggestion. The item may already be saved
 * (`itemId`) or still be mid-capture (send `name` + optional `tags`/`description`).
 * At least one of `itemId` or `name` must be provided.
 */
export const AIPlaceItemSchema = z
    .object({
    itemId: z.string().uuid().optional(),
    name: s(z.string().min(1).max(200)).optional(),
    tags: z.array(s(z.string().min(1).max(50))).max(20).optional(),
    description: sOpt,
})
    .refine((v) => Boolean(v.itemId) || Boolean(v.name), {
    message: "Provide either an itemId or a name",
    path: ["name"],
});
// --- Phase 3: Maturity ---
export const CreateProcessTrialSchema = z.object({
    title: s(z.string().min(1).max(200)),
    description: sOpt,
    frequency: z.enum(flowFrequencies),
    estimatedMinutes: z.number().int().min(1).max(480).nullable().optional(),
    tasks: z.array(z.object({
        id: z.string(),
        label: s(z.string().min(1)),
        isCompleted: z.boolean().default(false),
    })).min(1).max(20),
});
export const AISuggestGoalsSchema = z.object({
    limit: z.number().int().min(1).max(5).default(3),
});
export const GoalHistoryFilterSchema = z.object({
    status: z.enum(["achieved", "paused", "abandoned"]).optional(),
    limit: z.number().int().min(1).max(50).default(20),
    offset: z.number().int().min(0).default(0),
});
export const DeclutterSuggestionSchema = z.object({
    daysUnused: z.number().int().min(30).max(3650).default(180),
});
/**
 * Flow #1 — "Reorganize a selected set across selected containers".
 * The user hands us the exact scope: which items to redistribute and which
 * containers they must fit across. containerType is "box" for v1.
 */
export const AIReorganizeSchema = z.object({
    itemIds: z.array(z.string().uuid()).min(1).max(200),
    containerIds: z.array(z.string().uuid()).min(1).max(50),
    // "container_space" = a space of type "container" (closet/drawer/shelf); the
    // preferred target. "box" = a seasonal box (legacy path, still supported).
    containerType: z.enum(["container_space", "box"]).default("container_space"),
});
/** Body for persisting/estimating dimensions on an item or box (Flow #1). */
export const SaveDimensionsSchema = z.object({
    lengthMm: z.number().positive().max(5000),
    widthMm: z.number().positive().max(5000),
    heightMm: z.number().positive().max(5000),
    dimensionSource: z.enum(["ai_estimated", "user_entered", "catalog"]).default("user_entered"),
    dimensionConfidence: z.number().min(0).max(1).optional(),
    compressible: z.boolean().optional(),
});
// --- Reorganization Suggestions ---
export const reorgSuggestionTypes = ["group_by_tag", "unused_in_active", "unlocated", "empty_space", "set_improvement_target"];
//# sourceMappingURL=index.js.map